import { useEffect, useMemo } from 'react'

import { AppLoader } from 'modules/app-registry/templates/FullPageAppTemplate/components'
import { useNavigate } from 'react-router-dom'
import { useCheckTokenQuery } from 'shared/api/auth/requests'
import { CustomEvents } from 'shared/constants/customEvents'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { useSearchParams } from 'shared/hooks/useSearchParams'
import { UserWithToken } from 'shared/types/user'
import { dispatchCustomEvent } from 'shared/utils/customEvents'
import { lsGetItem, lsSetItem } from 'shared/utils/local-storage'
import { validateRedirectUrl } from 'shared/utils/validateRedirectUrl'

import styles from './LoginWithTokenPage.module.scss'

export const LoginWithTokenPage = () => {
  const [{ token, redirect_url, refresh_token }] = useSearchParams<{
    token?: string
    refresh_token?: string
    redirect_url?: string
  }>()
  const navigate = useNavigate()
  const user = useMemo(() => lsGetItem(LocalStorageKey.USER), [])

  useEffect(() => {
    if (!token || !refresh_token) {
      navigate('/login')
    }
  }, [token, refresh_token, navigate])

  const { data, error } = useCheckTokenQuery(
    { token: token ?? '' },
    {
      refetchInterval: () => 60 * 1000 * 3,
      enabled: !!token,
      retry: false
    }
  )

  useEffect(() => {
    if (!data || !token || !refresh_token) return

    const updatedUserData: UserWithToken = {
      ...user,
      ...data.data.user,
      original_organization_id: data.data.original_organization_id,
      access_token: token,
      refresh_token: refresh_token,
      user_roles:
        user?.user_roles ?? data?.data?.user?.user_roles ?? data?.data?.roles
    }

    lsSetItem(LocalStorageKey.USER, updatedUserData)

    const currentSelectableUsers =
      lsGetItem(LocalStorageKey.SELECTABLE_USERS) ?? []
    const userSavedAlready = currentSelectableUsers.some(
      u => u.email === data?.data?.user?.email
    )

    if (!userSavedAlready) {
      lsSetItem(LocalStorageKey.SELECTABLE_USERS, [
        ...currentSelectableUsers,
        {
          avatar:
            user?.profile?.avatar || data?.data?.user?.profile?.avatar || '',
          email: user?.email || data?.data?.user?.email,
          id: crypto.randomUUID(),
          name: user?.name || data?.data?.user?.name
        }
      ])
    }

    dispatchCustomEvent(CustomEvents.AUTH_USER_DATA_UPDATED, {
      user: updatedUserData
    })

    const safeRedirectUrl = validateRedirectUrl(redirect_url, '/')

    dispatchCustomEvent(CustomEvents.AUTH_TOKEN_UPDATED, {
      access_token: token,
      refresh_token: refresh_token,
      redirectRoute: safeRedirectUrl
    })
  }, [data, user, token, refresh_token, redirect_url])

  useEffect(() => {
    if (error) {
      navigate('/login')
    }
  }, [error, navigate])

  return (
    <div className={styles.pageWrapper}>
      <AppLoader />
    </div>
  )
}
